import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Clock, MapPin, PlayCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Loader, RetellingBadge, ScriptureRef } from "@/components/cast/Common";
import { ListenAll } from "@/components/cast/ListenAll";
import { BibleMap, ViewToggle, useViewMode } from "@/components/cast/BibleMap";
import { EPISODE_PLACE, SERIES_PLACE } from "@/lib/bibleMap";
import { useAuth } from "@/context/AuthContext";

export function JourneyMap({ journey, episodes }: { journey: any[]; episodes: any[] }) {
  return (
    <div className="relative rounded-2xl bg-[#F3ECE0]/60 p-6" data-testid="journey-map">
      <p className="eyebrow">Journey map</p>
      <div className="mt-5 flex items-start gap-0">
        {journey.map((j, i) => {
          const done = episodes.find((e) => e.order === j.episode)?.completed;
          return (
            <div key={i} className="flex flex-1 flex-col items-center text-center">
              <div className="flex w-full items-center">
                <span className={`h-0.5 flex-1 ${i === 0 ? "opacity-0" : "border-t-2 border-dashed border-[#C86D51]/50"}`} />
                <span className={`flex h-10 w-10 items-center justify-center rounded-full ${done ? "bg-[#5B7065]" : "bg-white ring-2 ring-[#C86D51]"}`}><MapPin className={`h-4 w-4 ${done ? "text-white" : "text-[#C86D51]"}`} /></span>
                <span className={`h-0.5 flex-1 ${i === journey.length - 1 ? "opacity-0" : "border-t-2 border-dashed border-[#C86D51]/50"}`} />
              </div>
              <p className="mt-2 text-sm font-semibold text-slate-800">{j.place}</p>
              <p className="text-xs text-slate-500">Episode {j.episode}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Series() {
  const { seriesId } = useParams();
  const [s, setS] = useState<any>(null);
  const [view, setView] = useViewMode();
  const { user } = useAuth();
  useEffect(() => { api.get(`/series/${seriesId}`).then((r) => setS(r.data)); }, [seriesId]);
  if (!s) return <Loader />;
  return (
    <div className="space-y-10">
      <div className="grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-5"><img src={s.cover} alt={s.title} className="aspect-[4/3] w-full rounded-3xl object-cover shadow-xl" /></div>
        <div className="flex flex-col justify-center lg:col-span-7">
          <p className="eyebrow">Boat · {s.character}</p>
          <h1 className="mt-3 font-display text-4xl font-semibold text-slate-900 sm:text-5xl lg:text-6xl" data-testid="series-title">{s.title}</h1>
          <p className="mt-4 text-lg text-slate-600">{s.summary}</p>
          <div className="mt-5 flex flex-wrap items-center gap-3"><RetellingBadge refText={s.book} /><ListenAll title={s.title} episodeIds={s.episodes.map((e: any) => e.id)} load={async (id) => (await api.get(`/episodes/${id}`)).data} /></div>
          <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-950" data-testid="series-disclaimer">These episodes retell Bible events in simple words to help you engage. They are not Scripture itself: each scene shows the passage it comes from so you can read the original.</p>
        </div>
      </div>
      <div className="space-y-4" data-testid="series-journey-section">
        <ViewToggle value={view} onChange={setView} />
        {view === "map" ? <BibleMap zoom testId="series-map" celebrateKey={`cast_harbour_${user?.id}_${s.id}`} stops={s.episodes.map((e: any) => ({ key: e.id, place: EPISODE_PLACE[e.id] || SERIES_PLACE[s.id], label: `${e.order}. ${e.title}`, sub: e.ref, href: `/episode/${e.id}`, done: e.completed, image: e.image }))} />
          : <JourneyMap journey={s.journey} episodes={s.episodes} />}
      </div>
      <div className="space-y-4">
        {s.episodes.map((e: any) => (
          <div key={e.id} className="group relative flex items-center gap-5 rounded-2xl border border-[#E2DBCE] bg-white p-5 transition-all hover:border-[#C86D51] hover:shadow-md">
            <Link to={`/episode/${e.id}`} data-testid={`episode-row-${e.id}`} aria-label={e.title} className="absolute inset-0 rounded-2xl" />
            <span className="font-display text-4xl font-semibold text-[#C86D51]/60">{String(e.order).padStart(2, "0")}</span>
            <div className="flex-1">
              <h3 className="font-display text-2xl font-semibold text-slate-900">{e.title}</h3>
              <div className="relative z-10 mt-1 flex w-fit flex-wrap items-center gap-3 text-sm text-slate-500"><ScriptureRef refText={e.ref} /><span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{e.minutes} min</span></div>
            </div>
            {e.completed ? <CheckCircle2 className="h-7 w-7 text-[#5B7065]" data-testid={`episode-complete-${e.id}`} /> : <PlayCircle className="h-8 w-8 text-[#1E293B] transition-transform group-hover:scale-110" />}
          </div>
        ))}
      </div>
    </div>
  );
}
